import loadable from '@utils/loadable';
import {
  CRYPTOCURRENCIES,
  PRIVATE_COMPANIES,
  PUBLIC_COMPANIES,
  PEOPLE,
  COMMODITIES,
  INDICES,
  FOREIGN_EXCHANGE,
  EXCHANGE_TRADED_FUNDS,
  CLOSED_END_FUNDS,
  PRIVATE_INVESTOR,
  UK_SINGLE_COMPANIE,
} from './topicClassTypes';
import {
  LEVEL_1,
  LEVEL_2,
  LEVEL_3,
  LEVEL_4,
} from '../topicTypes';
import {
  INFORMATION,
  FILINGS,
  NEWS,
  CHARTS,
  SENTIMENT,
  INVESTOR_RELATIONS,
  ESG,
  INSIDER_TRANSACTIONS,
  CAPITAL_STRUCTURE,
  INVESTMENTS,
  DCSC_SECTORS,
  SMART_WATCHLIST,
} from './tabPaths';

const ChartsAndData = loadable(() => import('../../../components/SEO_pages/Topic/ChartsAndData'));
const News = loadable(() => import('../../../components/SEO_pages/News'));
const Info = loadable(() => import('../../../components/SEO_pages/Topic/Info'));
const Filings = loadable(() => import('../../../components/SEO_pages/Topic/Filings'));
const InvestorRelations = loadable(() => import('../../../components/SEO_pages/InvestorRelations'));
const ESGComponent = loadable(() => import('../../../components/SEO_pages/ESGComponent'));
const InsiderTransactionsTab = loadable(() => import('../../../components/SEO_pages/Topic/InsiderTransactionsTab'));
const SectorsTab = loadable(() => import('../../../components/SEO_pages/SectorsTab'));
const SmartWatchlist = loadable(() => import('../../../components/SEO_pages/Topic/SmartWatchlist'));
const CapitalStructure = loadable(() => import('../../../components/SEO_pages/Topic/CapitalStructure'));

const TopicSlugNews = loadable(() => import('../../../components/SEO_pages/TopicSlug/TopicSlugBody'));
const LocationsSentiment = loadable(() => import('../../../components/SEO_pages/LocationsSentiment'));

export const TopicTabs = {
  [NEWS]: News,
  [FILINGS]: Filings,
  [INVESTOR_RELATIONS]: InvestorRelations,
  [INSIDER_TRANSACTIONS]: InsiderTransactionsTab,
  [DCSC_SECTORS]: SectorsTab,
  [SMART_WATCHLIST]: SmartWatchlist,
  [ESG]: ESGComponent,
  [CHARTS]: ChartsAndData,
  [INFORMATION]: Info,
  [CAPITAL_STRUCTURE]: CapitalStructure,
};

export const TABS = [
  {
    id: 1,
    title: 'News',
    requiredProps: [
      'user', 'slug', 'mode',
      'slugType', 'assetType', 'isSummary',
    ],
    path: NEWS,
    isDefault: true,
  },
  {
    id: 2,
    title: 'Filings',
    requiredProps: [
      'filings', 'slug', 'assetType',
    ],
    path: FILINGS,
  },
  {
    id: 6,
    title: 'Investor Relations',
    requiredProps: [
      'slug',
    ],
    path: INVESTOR_RELATIONS,
  },
  {
    id: 8,
    title: 'Insider Transactions',
    requiredProps: [
      'slug', 'assetType',
    ],
    path: INSIDER_TRANSACTIONS,
    isBeta: false,
    isNew: true,
  },
  {
    id: 7,
    title: 'ESG',
    requiredProps: [
      'slug',
    ],
    path: ESG,
  },
  {
    id: 4,
    title: 'Charts & Data',
    requiredProps: [
      'assetType', 'slug', 'mode',
    ],
    path: CHARTS,
  },
  {
    id: 11,
    title: 'Sectors',
    requiredProps: [
      'assetType',
      'slug',
    ],
    path: DCSC_SECTORS,
    isDcsc: true,
  },
  {
    id: 12,
    title: 'Smart Watchlist',
    requiredProps: [
      'slug',
    ],
    path: SMART_WATCHLIST,
  },
  {
    id: 5,
    title: 'Info',
    requiredProps: [
      'slug', 'assetType',
    ],
    path: INFORMATION,
    isDefault: true,
  },
  {
    id: 9,
    title: 'Capital Structure',
    requiredProps: [
      'slug', 'assetType',
    ],
    path: CAPITAL_STRUCTURE,
    isNew: true,
    isBeta: true,
  },
  {
    id: 10,
    title: 'Investments',
    requiredProps: [
      'isLoading', 'singleInvestorData',
      'singleInvestorFields', 'collapsableTableData',
      'singleInvestorMobileFields', 'onSort',
      'mainWidth',
    ],
    path: INVESTMENTS,
    isBeta: true,
  },
].map((tab) => ({
  ...tab,
  active: false,
}));

export const ALLOWED_TABS = [
  {
    type: CRYPTOCURRENCIES,
    tabs: [1, 4, 11],
  },
  {
    type: PRIVATE_COMPANIES,
    tabs: [1, 2, 3, 6, 7, 5, 8, 9, 11],
  },
  {
    type: PUBLIC_COMPANIES,
    tabs: [1, 2, 3, 4, 6, 7, 5, 8, 11],
  },
  {
    type: PEOPLE,
    tabs: [1, 8, 11],
  },
  {
    type: COMMODITIES,
    tabs: [1, 4, 11],
  },
  {
    type: INDICES,
    tabs: [1, 4, 11],
  },
  {
    type: FOREIGN_EXCHANGE,
    tabs: [1, 4, 11],
  },
  {
    type: EXCHANGE_TRADED_FUNDS,
    tabs: [1, 4, 8, 11],
  },
  {
    type: CLOSED_END_FUNDS,
    tabs: [1, 8, 11],
  },
  {
    type: PRIVATE_INVESTOR,
    tabs: [10],
  },
  {
    type: UK_SINGLE_COMPANIE,
    tabs: [1, 2, 9],
  },
  ...[LEVEL_1, LEVEL_2, LEVEL_3, LEVEL_4].map((level) => ({
    type: level,
    tabs: [1, 12],
  })),
];

export const DEFAULT_TABS = {
  type: '',
  tabs: [1, 8, 11],
};

export const TOPIC_SLUG_TABS = [
  {
    id: 2,
    title: 'News',
    component: TopicSlugNews,
    requiredProps: [
      'user', 'slug', 'mode', 'slugType', 'piwikEnabled', 'parents', 'isSummary', 'hasNewsTab', 'isLocationsTab',
    ],
    path: NEWS,
    isDefault: true,
    componentName: 'TopicSlugNews',
  },
  {
    id: 6,
    title: 'Sentiment Analysis',
    component: LocationsSentiment,
    requiredProps: [
      'slug', 'slugType', 'isLocationsTab', 'parents',
    ],
    path: SENTIMENT,
    isBeta: false,
  },
].map((tab) => ({
  ...tab,
  active: false,
}));

export const TOPIC_SLUG_TYPES_TABS = [
  {
    type: 'LOCATION_TYPES',
    tabs: [2, 6],
  },
];

export const TOPIC_SLUG_DEFAULT_TABS = {
  type: '',
  tabs: [2],
};
